import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bookmark, Plus, Trash2, UploadCloud, UserCheck, Loader2, AtSign, Search, ChevronLeft, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";

const PAGE_SIZE = 50;
const TYPE_LABEL = { personal: "Person", business: "Business", capital: "Capital" };
const TYPE_CLS = {
  personal: "bg-teal-50 text-teal-700",
  business: "bg-indigo-50 text-indigo-700",
  capital: "bg-amber-50 text-amber-700",
};

function TypeBadge({ t }) {
  return <Badge variant="secondary" className={`shrink-0 border-0 font-[560] ${TYPE_CLS[t] || "bg-secondary"}`}>{TYPE_LABEL[t] || t}</Badge>;
}

// --- Assign-to-profile dialog (search the registry, pick a target) ---
function AssignDialog({ reservation, onClose, onAssigned }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!reservation) return;
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/handles?q=${encodeURIComponent(q)}&page=1&page_size=15`);
        if (active) setResults(data.items || []);
      } catch { if (active) setResults([]); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, reservation]);

  const assign = async (item) => {
    setAssigning(true);
    try {
      const { data } = await api.post(`/admin/reservations/${reservation.id}/assign`, {
        profile_type: item.profile_type, profile_id: item.profile_id,
      });
      const disp = data?.displaced || [];
      if (disp.length) toast.warning(`Displaced ${disp.length} holder(s): ${disp.map((x) => `${x.display_name || x.profile_id} → ${x.new_value}`).join(", ")}`);
      toast.success("Reservation assigned.");
      onAssigned();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
    finally { setAssigning(false); }
  };

  return (
    <Dialog open={!!reservation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reservation-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign reservation</DialogTitle>
          <DialogDescription>
            Force-assigns {reservation?.handle ? <span className="font-[600]">@{reservation.handle}</span> : null}
            {reservation?.handle && reservation?.slug ? " and " : null}
            {reservation?.slug ? <span className="font-mono">/{reservation.slug}</span> : null} to the chosen profile.
            If taken, the current holder is displaced to a random name.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a profile by handle or slug…" className="pl-9" autoFocus data-testid="reservation-assign-search" />
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-border/70 rounded-lg border border-border">
          {!results ? <div className="py-6"><Loading /></div> : results.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No profiles found.</p>
          ) : results.map((it) => (
            <div key={it.profile_id} className="flex items-center gap-2 px-3 py-2" data-testid={`reservation-assign-target-${it.profile_id}`}>
              <TypeBadge t={it.profile_type} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-[600] text-foreground">{it.display_name || "—"}</div>
                <div className="truncate text-xs text-muted-foreground">@{it.handle}{it.slug ? ` · /${it.slug}` : ""}</div>
              </div>
              <Button size="sm" disabled={assigning} onClick={() => assign(it)} data-testid={`reservation-assign-confirm-${it.profile_id}`}>
                {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserCheck className="mr-1.5 h-3.5 w-3.5" /> Assign</>}
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="secondary" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReservationsManager() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);

  const [form, setForm] = useState({ handle: "", slug: "", note: "" });
  const [creating, setCreating] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulking, setBulking] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileRef = useRef(null);

  const [assignTarget, setAssignTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const bump = () => setRefresh((k) => k + 1);

  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/reservations?q=${encodeURIComponent(q)}&page=${page}&page_size=${PAGE_SIZE}`);
      setItems(data.items || []); setTotal(data.total || 0);
    } catch { setItems([]); setTotal(0); }
  }, [q, page]);

  useEffect(() => { reload(); }, [reload, refresh]);

  const create = async () => {
    const handle = form.handle.trim();
    const slug = form.slug.trim();
    if (handle && !slug) { toast.error("Reserving a handle requires a slug."); return; }
    if (!handle && !slug) { toast.error("Enter a handle and/or a slug."); return; }
    setCreating(true);
    try {
      await api.post("/admin/reservations", { handle, slug, note: form.note.trim() });
      toast.success("Reserved.");
      setForm({ handle: "", slug: "", note: "" }); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reserve"); }
    finally { setCreating(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text); setBulkOpen(true); setBulkResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const runBulk = async () => {
    setBulking(true);
    try {
      const { data } = await api.post("/admin/reservations/bulk", { csv: csvText });
      setBulkResult(data);
      toast.success(`Reserved ${data.created_count} · skipped ${data.skipped.length}`);
      bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk upload failed"); }
    finally { setBulking(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/reservations/${deleteTarget.id}`);
      toast.success("Reservation released."); setDeleteTarget(null); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not release"); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="admin-reservations">
      {/* Create + CSV */}
      <Card className="border-0 p-5">
        <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground"><Bookmark className="h-5 w-5 text-primary" /> Reserve a name</h2>
        <p className="mt-1 text-xs text-muted-foreground">Reserving a handle requires a slug. A slug can be reserved on its own. Reserved names can't be claimed by members until you assign them.</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="relative">
            <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.replace(/^@+/, "").slice(0, 50) })} placeholder="handle (optional)" className="pl-9" data-testid="reservation-handle-input" />
          </div>
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 80) })} placeholder="slug" data-testid="reservation-slug-input" />
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="note (optional)" data-testid="reservation-note-input" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button onClick={create} disabled={creating} data-testid="reservation-create-button">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Reserve</>}</Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="reservation-csv-file" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="reservation-csv-upload"><UploadCloud className="mr-1.5 h-4 w-4" /> Bulk upload CSV</Button>
          <span className="text-xs text-muted-foreground">CSV columns: <span className="font-mono">handle, slug, note</span></span>
        </div>
      </Card>

      {/* List */}
      <Card className="border-0 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-[640] text-foreground">Reservations</h2>
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search reservations…" className="w-full max-w-xs" data-testid="reservation-search" />
        </div>
        {!items ? <div className="mt-6"><Loading /></div> : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{q ? "No reservations match." : "No reservations yet."}</p>
        ) : (
          <>
            <div className="mt-4 divide-y divide-border/70">
              {items.map((r) => (
                <div key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`reservation-row-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {r.handle && <span className="text-sm font-[600] text-primary">@{r.handle}</span>}
                      {r.slug && <span className="font-mono text-xs text-foreground">/{r.slug}</span>}
                      {r.status === "assigned"
                        ? <Badge variant="secondary" className="border-0 bg-emerald-50 text-emerald-700">Assigned{r.assigned_display_name ? ` · ${r.assigned_display_name}` : ""}</Badge>
                        : <Badge variant="secondary" className="border-0 bg-secondary text-muted-foreground">Reserved</Badge>}
                    </div>
                    {r.note && <div className="truncate text-xs text-muted-foreground">{r.note}</div>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setAssignTarget(r)} data-testid={`reservation-assign-${r.id}`}><UserCheck className="mr-1.5 h-3.5 w-3.5" /> Assign</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteTarget(r)} data-testid={`reservation-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{total} reservation{total === 1 ? "" : "s"} · page {page} of {pages}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="reservation-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="reservation-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <AssignDialog reservation={assignTarget} onClose={() => setAssignTarget(null)} onAssigned={() => { setAssignTarget(null); bump(); }} />

      {/* Bulk CSV dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="reservation-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk reserve from CSV</DialogTitle>
            <DialogDescription>Columns: <span className="font-mono">handle, slug, note</span>. A header row is optional. Reserving a handle requires a slug.</DialogDescription>
          </DialogHeader>
          <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={8} className="font-mono text-xs" placeholder={"handle,slug,note\nacme,acme,Brand\n,reservedslug,Slug only"} data-testid="reservation-bulk-textarea" />
          {bulkResult && (
            <div className="rounded-lg border border-border p-3 text-sm" data-testid="reservation-bulk-result">
              <p className="font-[600] text-emerald-700">Reserved {bulkResult.created_count}</p>
              {bulkResult.skipped.length > 0 && (
                <div className="mt-2">
                  <p className="font-[600] text-amber-700">Skipped {bulkResult.skipped.length}</p>
                  <ul className="mt-1 max-h-32 overflow-y-auto text-xs text-muted-foreground">
                    {bulkResult.skipped.map((s, i) => <li key={i}>Line {s.line}: {s.handle || "—"}/{s.slug || "—"} — {s.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setBulkOpen(false); setBulkResult(null); }}>Close</Button>
            <Button onClick={runBulk} disabled={bulking} data-testid="reservation-bulk-submit">{bulking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reserve all"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Release this reservation?</AlertDialogTitle>
            <AlertDialogDescription>Releasing frees {deleteTarget?.handle ? `@${deleteTarget.handle}` : ""}{deleteTarget?.handle && deleteTarget?.slug ? " and " : ""}{deleteTarget?.slug ? `/${deleteTarget.slug}` : ""} so members can claim it again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="reservation-delete-confirm">Release</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
